import { useCallback, useEffect, useState } from 'react';
import CampaignManagementQuery from '../../../services/query/campaignManagement/CampaignQuery.services';
import { ICouponFormData } from '../types/interfaces/CampaingTypes';
import { CampaignManagementCommand } from '../../../services/command';
import {
    CouponCharacterSetType,
    CouponExecutionType,
    CouponFormInput,
    CouponGenerationType,
    CouponStatus,
} from '../../../types/enums/campaign-enums';
import { enumSnackbarStatusType } from '../../../types/enums';
import { useTranslation } from 'react-i18next';
import Helper from '../../../utils/helper';
import { showSnackbar } from '../../../stores/redux/actions';
import { useDispatch } from 'react-redux';

export const StepCouponServiceComponent = ({ ...props }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const campaignService = props.campaignService;
    const {
        id,
        showLoading,
        hideLoading,
        campaignDetail,
        setCampaignDetail,
        updatedCachedVersion,
        setUpdatedCacheVersion,
        downloadFile,
        saveCampaignForm,
        advanceToNextStep,
    } = campaignService;

    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [coupons, setCoupons] = useState<any[]>([]);
    const [useCouponCampaign, setUseCouponCampaign] = useState<boolean>(false);
    const [useCouponCampaignDisabled, setUseCouponCampaignDisabled] = useState<boolean>(true);

    const [initialCouponForm, setInitialCouponForm] = useState<ICouponFormData>({
        id: null,
        campaignRuleId: parseInt(id!),
        couponName: '',
        couponCode: '',
        characterSet: 0,
        prefix: '',
        postFix: '',
        userFileId: null,
        status: 1,
        perCustomerCouponCount: null,
        totalCouponCount: null,
        couponType: 0,
    });

    const [formData, setFormData] = useState<ICouponFormData>(initialCouponForm);

    const [modalInfo, setModalInfo] = useState<any>({
        open: false,
        editMode: false,
    });

    const InputPermissons = [
        {
            key: CouponFormInput.CouponName,
            show: [
                CouponGenerationType.Unique,
                CouponGenerationType.Generic,
                CouponGenerationType.Personal,
                CouponGenerationType.Excel,
            ],
        },
        { key: CouponFormInput.CouponCode, show: [CouponGenerationType.Generic] },
        {
            key: CouponFormInput.CharacterSet,
            show: [CouponGenerationType.Unique, CouponGenerationType.Personal],
        },
        {
            key: CouponFormInput.Prefix,
            show: [CouponGenerationType.Unique, CouponGenerationType.Personal],
        },
        {
            key: CouponFormInput.Postfix,
            show: [CouponGenerationType.Unique, CouponGenerationType.Personal],
        },
        {
            key: CouponFormInput.TotalCouponCount,
            show: [CouponGenerationType.Unique, CouponGenerationType.Generic],
        },
        {
            key: CouponFormInput.Status,
            show: [
                CouponGenerationType.Unique,
                CouponGenerationType.Generic,
                CouponGenerationType.Personal,
                CouponGenerationType.Excel,
            ],
        },
        {
            key: CouponFormInput.UserFileId,
            show: [CouponGenerationType.Personal, CouponGenerationType.Excel],
        },
    ];

    const ContinueButtonDisabled =
        useCouponCampaign &&
        (coupons.length == 0 ||
            coupons.some((c) => c.executionStatus == CouponExecutionType.Queue) || // devam eden iş varsa butonu disabled yap
            !coupons.some((c) => c.executionStatus == CouponExecutionType.Completed)); // hiç hazır olan bir kayıt yoksa butonu disabled yap (başarılı hiç kayıt yok ve hatalı kayıt varsa butonu disabled yap)

    const getCoupons = async () => {
        const campaignId: number = parseInt(id!);
        if (campaignId > 0) {
            showLoading();
            setIsLoaded(false);

            let result = await CampaignManagementQuery.GetCoupons({
                Id: id,
                culture: Helper.getLang(),
            });

            if (result?.isSuccess && result.data != undefined) {
                setCoupons(result.data);
            }

            hideLoading();
            setIsLoaded(true);
        }
    };

    useEffect(() => {
        if (campaignDetail != undefined) {
            getCoupons();
            setUseCouponCampaign(campaignDetail?.campaignRule?.isCouponCampaign);
        }
    }, [campaignService && id]);

    useEffect(() => {
        if (isLoaded) {
            setUseCouponCampaign(coupons.length > 0);
            setUseCouponCampaignDisabled(coupons.length > 0);

            if (coupons.length > 0) {
                const firstCoupon = coupons[0];
                setFormData({
                    ...formData,
                    couponType: firstCoupon.generationType,
                    perCustomerCouponCount: firstCoupon.perCustomerCouponCount,
                });
            }
        }
    }, [coupons, isLoaded]);

    //First Input Fields on List

    const useCouponOnChange = (event: any) => {
        const { checked } = event.target;
        if (!checked) {
            setFormData({ ...formData, couponType: 0 });
        }

        setUseCouponCampaign(checked);
        setCampaignDetail({
            ...campaignDetail,
            campaignRule: {
                ...campaignDetail.campaignRule,
                isCouponCampaign: checked ? true : false,
            },
        });
    };

    const perCustomerCouponOnInput = (event: any) => {
        const { value } = event.target;

        event.target.value = !!value && Math.abs(value) >= 1 && Math.abs(value) <= 100000000 ? Math.abs(value) : null;
    };

    //List

    const getStatus = useCallback((status: number) => {
        switch (status) {
            case CouponStatus.Passive:
                return {
                    id: CouponStatus.Passive,
                    text: t('campaignManagement.save.step.coupon.item.couponStatus.passive'),
                    color: 'default',
                };
            case CouponStatus.Active:
                return {
                    id: CouponStatus.Active,
                    text: t('campaignManagement.save.step.coupon.item.couponStatus.active'),
                    color: 'success',
                };
        }
    }, []);

    const getExecutionStatus = useCallback((executionStatus: number, message: string) => {
        switch (executionStatus) {
            case CouponExecutionType.Queue:
                return {
                    id: CouponExecutionType.Queue,
                    text: t('campaignManagement.save.step.coupon.item.processStatus.processing'),
                    message: message,
                    color: 'primary',
                    textColor: 'white',
                };
            case CouponExecutionType.Completed:
                return {
                    id: CouponExecutionType.Completed,
                    text: t('campaignManagement.save.step.coupon.item.processStatus.completed'),
                    message: message,
                    color: 'success',
                    textColor: 'white',
                };
            case CouponExecutionType.Error:
                return {
                    id: CouponExecutionType.Error,
                    text: t('campaignManagement.save.step.coupon.item.processStatus.error'),
                    message: message,
                    color: 'error',
                    textColor: 'white',
                };
        }
    }, []);

    const getCharacterSet = useCallback((characterSet: number) => {
        switch (characterSet) {
            case CouponCharacterSetType.Latin:
                return t('campaignManagement.save.step.coupon.form.characterSetOption.latin');
            case CouponCharacterSetType.Kiril:
                return t('campaignManagement.save.step.coupon.form.characterSetOption.kiril');
            case CouponCharacterSetType.Arabic:
                return t('campaignManagement.save.step.coupon.form.characterSetOption.arabic');
            case CouponCharacterSetType.Georgian:
                return t('campaignManagement.save.step.coupon.form.characterSetOption.georgian');
        }
    }, []);

    //modal

    const editMode = (couponId: number) => {
        if (couponId <= 0) return;

        let couponItem = coupons.find((c) => c.id == couponId);
        if (couponItem == null) return;

        setFormData({
            ...formData,
            id: couponItem.id,
            // campaignRuleId: couponItem.campaignRuleId,
            couponName: couponItem.couponName,
            couponCode: couponItem.couponCode,
            characterSet: couponItem.characterSet,
            prefix: couponItem.prefix,
            postFix: couponItem.postFix,
            userFileId: couponItem.userFileId,
            status: couponItem.status,
            perCustomerCouponCount: couponItem.perCustomerCouponCount,
            totalCouponCount: couponItem.totalCouponCount,
            couponType: couponItem.generationType,
        });

        setModalInfo({
            ...modalInfo,
            open: true,
            editMode: true,
        });
    };

    const couponModalOpen = (id: number = 0) => {
        if (id == 0) {
            setModalInfo({ ...modalInfo, open: true });
        } else {
            editMode(id);
        }
    };

    const couponModalClose = () => {
        setFormData(initialCouponForm);
        setModalInfo({
            ...modalInfo,
            open: false,
            editMode: false,
        });
    };

    const couponModalSave = async (formValue: any) => {
        let result: any;
        let payload: any = {
            id: formValue.id.value ?? 0,
            campaignRuleId: formData.campaignRuleId,
            couponName: formValue.couponName.value,
            status: formValue.status.value ? CouponStatus.Active : CouponStatus.Passive,
            perCustomerCouponCount: formData.perCustomerCouponCount,
            totalCouponCount: formValue.totalCouponCount.value,
        };

        switch (formData.couponType) {
            case CouponGenerationType.Unique:
                payload = {
                    ...payload,
                    characterSet: formValue.characterSet.value,
                    prefix: formValue.prefix.value,
                    postFix: formValue.postFix.value,
                };

                result = await CampaignManagementCommand.SaveUniqueCoupon(payload);
                break;
            case CouponGenerationType.Generic:
                payload = {
                    ...payload,
                    couponCode: formValue.couponCode.value,
                };

                result = await CampaignManagementCommand.SaveMultipleCoupon(payload);
                break;
            case CouponGenerationType.Personal:
                payload = {
                    ...payload,
                    characterSet: formValue.characterSet.value,
                    prefix: formValue.prefix.value,
                    postFix: formValue.postFix.value,
                    userFileId: formValue.userFileId.value,
                };

                result = await CampaignManagementCommand.SaveMultipleCouponCustomer(payload);

                break;
            case CouponGenerationType.Excel:
                payload = {
                    ...payload,
                    characterSet: CouponCharacterSetType.Latin,
                    userFileId: formValue.userFileId.value,
                };

                result = await CampaignManagementCommand.SaveThirdPartyCoupon(payload);
                break;
        }

        setModalInfo({
            ...modalInfo,
            open: false,
        });

        let { isSuccess } = result;
        if (isSuccess) {
            await getCoupons();
            await saveCampaignForm({ isCouponCampaign: true });

            setUpdatedCacheVersion({
                ...updatedCachedVersion,
                reloadCampaignRuleCoupons: true,
            });
        }

        setFormData(initialCouponForm);
    };

    const downloadCouponFile = async (couponId: number) => {
        try {
            showLoading();
            const response = await CampaignManagementCommand.DownloadFile({
                CouponDefinitionId: couponId,
            });
            const { isSuccess, data } = response;
            if (!isSuccess) {
                hideLoading();
                dispatch(
                    showSnackbar({
                        message: t('campaignManagement.save.step.coupon.form.file.errorMessage.downloadErrorMessage'),
                        status: enumSnackbarStatusType.danger,
                    })
                );

                return false;
            }

            await downloadFile(data);
            hideLoading();
        } catch (error) {
            hideLoading();
            dispatch(
                showSnackbar({
                    message: `${t(
                        'campaignManagement.save.step.coupon.form.file.errorMessage.downloadErrorMessage'
                    )} : ${error}`,
                    status: enumSnackbarStatusType.danger,
                })
            );
        }
    };

    const saveForm = async (event: any) => {
        event.preventDefault();

        const saveResult = await saveCampaignForm({});
        if (saveResult) {
            advanceToNextStep();
        }
    };

    return {
        ...campaignService,
        coupons,
        initialCouponForm,
        setInitialCouponForm,
        formData,
        setFormData,
        useCouponCampaign,
        useCouponCampaignDisabled,
        InputPermissons,
        ContinueButtonDisabled,
        modalInfo,
        useCouponOnChange,
        perCustomerCouponOnInput,
        getCoupons,
        getStatus,
        getExecutionStatus,
        getCharacterSet,
        downloadCouponFile,
        couponModalOpen,
        couponModalClose,
        couponModalSave,
        saveForm,
    };
};

export const StepCouponTimerComponent = ({ ...props }) => {
    const { id, campaignService, modalInfo, coupons, getCoupons } = props;

    const RetryTimeValue = 10 * 1000; //default 5 sn

    const couponIsNotReady = () => {
        return coupons.some((c: any) => c.executionStatus != CouponExecutionType.Completed);
    };

    useEffect(() => {
        if (coupons.length > 0) {
            couponIsNotReady();
        }
    }, [coupons]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            if (!modalInfo.open && couponIsNotReady()) {
                getCoupons();
            }
        }, RetryTimeValue);

        return () => clearInterval(intervalId);
    }, [campaignService != undefined && id && !modalInfo.open && couponIsNotReady()]);

    return <></>;
};
